
import { Component, Vue, Watch } from 'vue-property-decorator';

import SelectField from '../../../../../framework/fields/SelectField.vue';
import { sharedState } from '../../../../../framework/state';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../../framework/client/resource';
import { getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';

import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';

import { SalesInvoice } from '../../../../../../common/application/model/SalesInvoice';
import { getCustomerOptions, getVehicleContractOptions } from '../../../../service/application_options_service';
import { postSalesInvoice, getSalesInvoiceById } from '../../../../client/netvisor';
import { SalesInvoiceState } from '../../../../../../common/application/model/enums/SalesInvoiceState';
import { SalesInvoiceType } from '../../../../../../common/application/model/enums/SalesInvoiceType';
import { SalesInvoiceRow } from '../../../../../../common/application/model/SalesInvoiceRow';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';

import { errorToast, successToast } from '../../../../service/toast_service';
import { getTagTypeFromSalesInvoiceState } from '../../../../service/tag_type_service';
import { FrameworkUserRole } from '../../../../../../common/framework/enumeration/FrameworkUserRole';
import { ApplicationUserRole } from '../../../../../../common/application/enumeration/ApplicationUserRole';
import ParameterService from '../../../../service/parameter_service';

@Component({
    components: {
        SelectField,
        AutoCompleteMultipleField,
    },
})
export default class SalesInvoices extends Vue {
    readonly resourceType = ApplicationResource.SALES_INVOICE;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as SalesInvoice[],
        total: 0,
        loading: false,
        selected: undefined as SalesInvoice | undefined,
        selectedCustomerId: undefined as string | undefined,
        selectedSalesInvoiceState: undefined as string | undefined,
        selectedVehicleContractId: undefined as string | undefined,
        customerOptions: [] as { id: string | undefined; label: string }[],
        salesInvoiceTypeOptions: [] as { id: string | undefined; label: string }[],
        salesInvoiceStateOptions: [] as { id: string | undefined; label: string }[],
        vehicleContractOptions: [] as { id: string | undefined; label: string }[],
        result: '',
        parameters: this.parameterService.get(),
    };

    async mounted() {
        await this.loadAsyncData();
        this.local.customerOptions = await getCustomerOptions();
        this.local.salesInvoiceTypeOptions = getEnumerationOptions(
            'SalesInvoiceType',
            Object.keys(SalesInvoiceType),
            this,
        );
        this.local.salesInvoiceStateOptions = getEnumerationOptions(
            'SalesInvoiceState',
            Object.keys(SalesInvoiceState),
            this,
        );
        this.local.vehicleContractOptions = await getVehicleContractOptions(this.local.parameters.customerId || []);
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();
        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;

        this.local.rows = [];

        const rows: SalesInvoice[] = await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        );

        rows.forEach((row) => {
            this.local.rows.push(row);
        });

        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }

        this.local.loading = false;
    }

    async onSortChange(field: string, order: 'desc' | 'asc') {
        this.local.parameters.sortField = field;
        this.local.parameters.sortOrder = order;
        await this.resetPagingAndLoadData();
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    @Watch('local.parameters.salesInvoiceState')
    async selectedSalesInvoiceStateChange() {
        if (this.local.loading) {
            return;
        }

        await this.resetPagingAndLoadData();
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    add() {
        this.$router.push(`/${this.resourceType}/add`);
    }

    addMonthlyInvoice() {
        this.$router.push(`/${this.resourceType}/add-monthly`);
    }

    addNextMonthlyInvoice() {
        this.$router.push(`/${this.resourceType}/add-next-monthly`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    view() {
        if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        const rows = await getResourcesNew<SalesInvoiceRow>(
                            ApplicationResource.SALES_INVOICE_ROW,
                            -1,
                            new Map([['salesInvoiceId', this.local.selected.id]]),
                        );
                        for (const row of rows) {
                            await deleteResource(ApplicationResource.SALES_INVOICE_ROW, row.id);
                        }
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');

                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    async sendToNetvisor(invoiceId: string) {
        try {
            this.local.loading = true;
            const result = await postSalesInvoice(invoiceId);

            this.local.loading = false;

            if (result.responseStatus.status !== 'OK') {
                errorToast(this, result.replies.insertedDataIdentifier);
            }

            await getSalesInvoiceById(invoiceId);
            await this.loadAsyncData();
        } catch (error) {
            this.local.loading = false;
        }
    }

    async getNetvisorInfo(invoiceId: string) {
        await getSalesInvoiceById(invoiceId);
        await this.loadAsyncData();
    }

    getCustomerName(id: string): string {
        return getOptionLabel(id, this.local.customerOptions);
    }

    getSalesInvoiceTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.salesInvoiceTypeOptions);
    }

    getSalesInvoiceStateLabel(id: string): string {
        return getOptionLabel(id, this.local.salesInvoiceStateOptions);
    }

    dateToString(date: string | undefined): string {
        if (!date) {
            return '';
        }

        return dateToDbDateString(new Date(date));
    }

    getTagType(id: SalesInvoiceState): string {
        return getTagTypeFromSalesInvoiceState(id);
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get isManager(): boolean {
        return this.shared.hasRole(ApplicationUserRole.MANAGER);
    }

    get customerIds(): string[] {
        return this.local.parameters.customerId || [];
    }

    get salesInvoiceId(): string[] {
        return this.local.parameters.id ? [this.local.parameters.id] : [];
    }

    onRowClick(row: SalesInvoice): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
